import React, { useState } from 'react';
import { 
  FolderTree, 
  Tags, 
  Plus, 
  Edit3, 
  Trash2, 
  RotateCcw, 
  Check, 
  X, 
  Leaf, 
  Droplets, 
  Sparkles, 
  ShieldCheck, 
  Package, 
  Flame, 
  Heart, 
  Stethoscope, 
  Sun, 
  Coffee, 
  Activity, 
  Pill, 
  Zap,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Sliders,
  ArrowUpDown,
  LayoutGrid,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';
import ImageUploadField from './ImageUploadField';

export const ICON_MAP = {
  Leaf,
  Droplets,
  Sparkles,
  ShieldCheck,
  Package,
  Flame,
  Heart,
  Stethoscope,
  Sun,
  Coffee,
  Activity,
  Pill,
  Zap
};

export const COLOR_THEMES = [
  { id: 'emerald', label: 'زمردی سبز (Emerald)', bgGradient: 'from-emerald-800 to-emerald-950', accentColor: 'text-emerald-400' },
  { id: 'teal', label: 'فیروزی (Teal)', bgGradient: 'from-teal-800 to-teal-950', accentColor: 'text-teal-400' },
  { id: 'amber', label: 'زعفرانی / عنبر (Amber)', bgGradient: 'from-amber-900 to-amber-950', accentColor: 'text-amber-400' },
  { id: 'stone', label: 'سرمئی خاکی (Stone)', bgGradient: 'from-stone-800 to-stone-950', accentColor: 'text-stone-300' },
  { id: 'green', label: 'تازہ ہربل (Herbal Green)', bgGradient: 'from-green-900 to-emerald-950', accentColor: 'text-green-400' },
  { id: 'rose', label: 'گلابی سرخی (Rose)', bgGradient: 'from-rose-900 to-rose-950', accentColor: 'text-rose-400' },
  { id: 'purple', label: 'جامنی اکسیر (Purple)', bgGradient: 'from-purple-900 to-purple-950', accentColor: 'text-purple-400' },
  { id: 'sky', label: 'آسمانی نیلا (Sky Blue)', bgGradient: 'from-sky-900 to-blue-950', accentColor: 'text-sky-400' }
];

const CategoryManager = () => {
  const { lang, isRtl } = useLanguage();
  const { 
    categories, 
    ailments, 
    mizajList, 
    products,
    addCategory, 
    updateCategory, 
    deleteCategory, 
    resetCategoriesToDefaults,
    addAilment,
    updateAilment,
    deleteAilment,
    resetAilmentsToDefaults,
    updateMizaj,
    resetMizajToDefaults
  } = useStore();

  const [activeSubTab, setActiveSubTab] = useState('categories'); // 'categories', 'ailments', 'mizaj'

  // --- Category Modal States ---
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    id: '',
    nameUrdu: '',
    nameEn: '',
    taglineUrdu: '',
    taglineEn: '',
    icon: 'Leaf',
    image: '',
    bgGradient: 'from-emerald-800 to-emerald-950',
    accentColor: 'text-emerald-400',
    itemCount: '50+ اشیاء',
    showOnHome: true,
    showRowOnHome: true,
    order: 1
  });

  // --- Ailment Modal States ---
  const [ailmentModalOpen, setAilmentModalOpen] = useState(false);
  const [editingAilment, setEditingAilment] = useState(null);
  const [ailmentForm, setAilmentForm] = useState({
    id: '',
    nameUrdu: '',
    nameEn: ''
  });

  // --- Mizaj Modal States ---
  const [mizajModalOpen, setMizajModalOpen] = useState(false);
  const [editingMizaj, setEditingMizaj] = useState(null);
  const [mizajForm, setMizajForm] = useState({
    id: '',
    nameUrdu: '',
    nameEn: ''
  });

  // Notifications
  const [actionAlert, setActionAlert] = useState(null);
  const showAlert = (msg, type = 'success') => {
    setActionAlert({ msg, type });
    setTimeout(() => setActionAlert(null), 3000);
  };

  // Open Category Modal for Add
  const handleOpenAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      id: '',
      nameUrdu: '',
      nameEn: '',
      taglineUrdu: '',
      taglineEn: '',
      icon: 'Leaf',
      image: '',
      bgGradient: 'from-emerald-800 to-emerald-950',
      accentColor: 'text-emerald-400',
      itemCount: '25+ اشیاء',
      showOnHome: true,
      showRowOnHome: true,
      order: (categories?.length || 0) + 1
    });
    setCategoryModalOpen(true);
  };

  // Open Category Modal for Edit
  const handleOpenEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({
      id: cat.id,
      nameUrdu: cat.nameUrdu || '',
      nameEn: cat.nameEn || '',
      taglineUrdu: cat.taglineUrdu || '',
      taglineEn: cat.taglineEn || '',
      icon: cat.icon || 'Leaf',
      image: cat.image || '',
      bgGradient: cat.bgGradient || 'from-emerald-800 to-emerald-950',
      accentColor: cat.accentColor || 'text-emerald-400',
      itemCount: cat.itemCount || '50+ اشیاء',
      showOnHome: cat.showOnHome !== false,
      showRowOnHome: cat.showRowOnHome !== false,
      order: cat.order !== undefined ? cat.order : 1
    });
    setCategoryModalOpen(true);
  };

  // Save Category
  const handleSaveCategory = (e) => {
    e.preventDefault();
    if (!categoryForm.nameUrdu.trim()) {
      alert('براہ کرم کیٹیگری کا اردو نام درج کریں۔');
      return;
    }

    if (editingCategory) {
      updateCategory({
        ...editingCategory,
        ...categoryForm
      });
      showAlert('کیٹیگری میں تبدیلیاں کامیابی سے محفوظ ہو گئیں!');
    } else {
      // Generate ID slug if not entered
      let slug = categoryForm.id ? categoryForm.id.trim() : '';
      if (!slug) {
        slug = categoryForm.nameEn 
          ? categoryForm.nameEn.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
          : `cat-${Date.now()}`;
      }
      addCategory({
        ...categoryForm,
        id: slug
      });
      showAlert('نئی کیٹیگری کامیابی سے شامل کر دی گئی!');
    }
    setCategoryModalOpen(false);
  };

  // Delete Category with warning
  const handleDeleteCategory = (cat) => {
    const assignedProducts = (products || []).filter(p => p.category === cat.id);
    let confirmMsg = `کیا آپ واقعی کیٹیگری "${cat.nameUrdu}" کو حذف کرنا چاہتے ہیں؟`;
    if (assignedProducts.length > 0) {
      confirmMsg += `\n\nتنبیہ: اس کیٹیگری کے ساتھ ${assignedProducts.length} پروڈکٹس منسلک ہیں۔ اگر آپ اسے ڈیلیٹ کریں گے تو وہ غیر زمرہ بند ہو جائیں گی۔`;
    }
    if (window.confirm(confirmMsg)) {
      deleteCategory(cat.id);
      showAlert(`کیٹیگری "${cat.nameUrdu}" حذف کر دی گئی۔`, 'info');
    }
  };

  // --- Ailment Handlers ---
  const handleOpenAddAilment = () => {
    setEditingAilment(null);
    setAilmentForm({ id: '', nameUrdu: '', nameEn: '' });
    setAilmentModalOpen(true);
  };

  const handleOpenEditAilment = (ail) => {
    setEditingAilment(ail);
    setAilmentForm({ id: ail.id, nameUrdu: ail.nameUrdu || '', nameEn: ail.nameEn || '' });
    setAilmentModalOpen(true);
  };

  const handleSaveAilment = (e) => {
    e.preventDefault();
    if (!ailmentForm.nameUrdu.trim()) {
      alert('براہ کرم مرض یا شکایت کا اردو نام درج کریں۔');
      return;
    }
    if (editingAilment) {
      updateAilment({ ...editingAilment, ...ailmentForm });
      showAlert('مرض / شکایت کا نام محفوظ ہو گیا!');
    } else {
      let slug = ailmentForm.id ? ailmentForm.id.trim() : '';
      if (!slug) {
        slug = ailmentForm.nameEn 
          ? ailmentForm.nameEn.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
          : `ailment-${Date.now()}`;
      }
      addAilment({ ...ailmentForm, id: slug });
      showAlert('نیا مرض فلٹر کامیابی سے شامل کر دیا گیا!');
    }
    setAilmentModalOpen(false);
  };

  const handleDeleteAilment = (ail) => {
    const taggedCount = (products || []).filter(p => (p.ailments || []).includes(ail.id)).length;
    let msg = `کیا آپ واقعی مرض فلٹر "${ail.nameUrdu}" کو حذف کرنا چاہتے ہیں؟`;
    if (taggedCount > 0) {
      msg += `\n(${taggedCount} پروڈکٹس اس مرض کے ساتھ منسلک ہیں)`;
    }
    if (window.confirm(msg)) {
      deleteAilment(ail.id);
      showAlert(`مرض فلٹر "${ail.nameUrdu}" حذف کر دی گیا۔`, 'info');
    }
  };

  // --- Mizaj Handlers ---
  const handleOpenEditMizaj = (m) => {
    setEditingMizaj(m);
    setMizajForm({ id: m.id, nameUrdu: m.nameUrdu || '', nameEn: m.nameEn || '' });
    setMizajModalOpen(true);
  };

  const handleSaveMizaj = (e) => {
    e.preventDefault();
    if (!mizajForm.nameUrdu.trim()) return;
    updateMizaj({ ...editingMizaj, ...mizajForm });
    showAlert('مزاج کی تفصیلات محفوظ ہو گئیں!');
    setMizajModalOpen(false);
  };

  // Preview Icon Component for category modal
  const PreviewIcon = ICON_MAP[categoryForm.icon] || Leaf;

  return (
    <div className="space-y-6">
      
      {/* Alert toast */}
      {actionAlert && (
        <div className={`p-4 rounded-2xl flex items-center justify-between shadow-lg text-xs font-urdu border animate-fade-in ${
          actionAlert.type === 'info' 
            ? 'bg-blue-50 text-blue-900 border-blue-200' 
            : 'bg-emerald-50 text-emerald-900 border-emerald-200'
        }`}>
          <div className="flex items-center gap-2 font-bold">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span>{actionAlert.msg}</span>
          </div>
          <button onClick={() => setActionAlert(null)} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Top Header Card */}
      <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold font-title mb-2">
            <FolderTree className="w-3.5 h-3.5" />
            <span>زیرو کوڈ کیٹیگری و فلٹرز کنٹرول سینٹر</span>
          </div>
          <h2 className="text-2xl font-black text-gray-900 font-title">
            اقسام ادویات، امراض و مزاج مینیجر
          </h2>
          <p className="text-xs text-gray-500 font-urdu mt-1 max-w-2xl">
            یہاں سے آپ سٹور کی تمام کیٹیگریز اور فلٹرز کا انتظام بغیر کوڈ چھوئے براہِ راست کر سکتے ہیں۔ نئی کیٹیگریز بنائیں، نام بدلیں، آئیکون منتخب کریں یا پرانی کیٹیگریز کو حذف کریں۔
          </p>
        </div>

        <div className="flex items-center gap-2">
          {activeSubTab === 'categories' && (
            <button
              type="button"
              onClick={handleOpenAddCategory}
              className="py-2.5 px-5 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs shadow-md transition flex items-center gap-2 cursor-pointer font-urdu"
            >
              <Plus className="w-4 h-4 text-amber-300" />
              <span>نئی کیٹیگری بنائیں</span>
            </button>
          )}

          {activeSubTab === 'ailments' && (
            <button
              type="button"
              onClick={handleOpenAddAilment}
              className="py-2.5 px-5 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs shadow-md transition flex items-center gap-2 cursor-pointer font-urdu"
            >
              <Plus className="w-4 h-4 text-amber-300" />
              <span>نیا مرض / شکایت شامل کریں</span>
            </button>
          )}
        </div>
      </div>

      {/* Sub-Tabs Navigation */}
      <div className="flex border-b border-gray-200 bg-white rounded-2xl p-1 shadow-sm">
        <button
          onClick={() => setActiveSubTab('categories')}
          className={`flex-1 py-2 px-4 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 font-urdu ${
            activeSubTab === 'categories'
              ? 'bg-pansar-800 text-white shadow-sm'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <FolderTree className="w-4 h-4" />
          <span>1. اقسام ادویات (Categories) ({(categories || []).length})</span>
        </button>

        <button
          onClick={() => setActiveSubTab('ailments')}
          className={`flex-1 py-2 px-4 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 font-urdu ${
            activeSubTab === 'ailments'
              ? 'bg-pansar-800 text-white shadow-sm'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Heart className="w-4 h-4" />
          <span>2. امراض و شکایات فلٹرز (Ailments) ({(ailments || []).length})</span>
        </button>

        <button
          onClick={() => setActiveSubTab('mizaj')}
          className={`flex-1 py-2 px-4 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 font-urdu ${
            activeSubTab === 'mizaj'
              ? 'bg-pansar-800 text-white shadow-sm'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Flame className="w-4 h-4" />
          <span>3. قانون مفرد اعضاء و مزاج (Mizaj Profiles) ({(mizajList || []).length})</span>
        </button>
      </div>

      {/* SUB-TAB 1: CATEGORIES LIST & CARDS */}
      {activeSubTab === 'categories' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between text-xs text-gray-500 font-urdu px-2">
            <span>کل فعال کیٹیگریز: <strong>{(categories || []).length}</strong></span>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('کیا آپ کیٹیگریز کو اصل حالت پر ری سیٹ کرنا چاہتے ہیں؟')) {
                  resetCategoriesToDefaults();
                  showAlert('کیٹیگریز اصل ڈیفالٹ پر ری سیٹ ہو گئیں!');
                }
              }}
              className="text-rose-600 hover:text-rose-800 flex items-center gap-1 cursor-pointer"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>ڈیفالٹ کیٹیگریز بحال کریں</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {(categories || []).map((cat) => {
              const IconComp = ICON_MAP[cat.icon] || Leaf;
              const prodCount = (products || []).filter(p => p.category === cat.id).length;

              return (
                <div 
                  key={cat.id}
                  className="bg-white rounded-3xl p-6 shadow-sm border border-gray-200 hover:border-pansar-400 transition-all flex flex-col justify-between group"
                >
                  <div>
                    {/* Top row: Icon & product badge */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-2xl bg-pansar-50 group-hover:bg-pansar-700 text-pansar-800 group-hover:text-amber-300 flex items-center justify-center transition-colors shadow-inner overflow-hidden">
                        {cat.image ? (
                          <img src={cat.image} alt={cat.nameUrdu} className="w-full h-full object-cover" />
                        ) : (
                          <IconComp className="w-6 h-6" />
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-pansar-100 text-pansar-800 font-urdu">
                          {prodCount} پروڈکٹس
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-gray-500 font-bold bg-gray-100 px-2 py-0.5 rounded-md font-mono" title="ہوم پیج پر ڈسپلے ترتیب">
                            #{cat.order || 1}
                          </span>
                          <span className="text-[10px] text-gray-400 font-title">
                            {cat.itemCount || '0 اشیاء'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Titles */}
                    <h3 className="text-lg font-black text-gray-900 font-title mb-1">
                      {cat.nameUrdu}
                    </h3>
                    <p className="text-xs font-semibold text-gray-500 font-sans mb-2">
                      {cat.nameEn}
                    </p>

                    {/* Slug */}
                    <div className="text-[11px] text-gray-400 font-mono bg-gray-50 px-2 py-1 rounded-md inline-block mb-3">
                      ID: {cat.id}
                    </div>

                    {/* Tagline */}
                    <p className="text-xs text-gray-600 font-urdu leading-relaxed line-clamp-2 mb-4">
                      {cat.taglineUrdu || cat.taglineEn || 'کوئی تفصیل درج نہیں ہے۔'}
                    </p>

                    {/* Home Visibility Quick 1-Click Toggles */}
                    <div className="bg-gray-50/80 p-2.5 rounded-2xl border border-gray-100 space-y-2">
                      <div className="text-[10px] font-bold text-gray-500 font-title flex items-center justify-between">
                        <span>ہوم پیج کنٹرول (1-Click Toggles)</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        {/* 1-Click Home Grid Toggle */}
                        <button
                          type="button"
                          onClick={() => {
                            const nextVal = cat.showOnHome === false ? true : false;
                            updateCategory({ ...cat, showOnHome: nextVal });
                            showAlert(`کیٹیگری "${cat.nameUrdu}" ہوم گرڈ میں ${nextVal ? 'شامل' : 'چھپا'} دی گئی۔`);
                          }}
                          className={`py-1.5 px-2 rounded-xl text-[11px] font-bold border transition flex items-center justify-center gap-1 font-urdu cursor-pointer ${
                            cat.showOnHome !== false
                              ? 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100'
                              : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-100'
                          }`}
                          title="ہوم پیج پر کیٹیگری کے ڈبے کو دکھائیں یا چھپائیں"
                        >
                          {cat.showOnHome !== false ? <LayoutGrid className="w-3.5 h-3.5 text-emerald-600" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                          <span>گرڈ: {cat.showOnHome !== false ? 'آن' : 'آف'}</span>
                        </button>

                        {/* 1-Click Home Slider Row Toggle */}
                        <button
                          type="button"
                          onClick={() => {
                            const nextVal = cat.showRowOnHome === false ? true : false;
                            updateCategory({ ...cat, showRowOnHome: nextVal });
                            showAlert(`کیٹیگری "${cat.nameUrdu}" پروڈکٹ سلائیڈر ${nextVal ? 'فعال' : 'چھپا'} دیا گیا۔`);
                          }}
                          className={`py-1.5 px-2 rounded-xl text-[11px] font-bold border transition flex items-center justify-center gap-1 font-urdu cursor-pointer ${
                            cat.showRowOnHome !== false
                              ? 'bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100'
                              : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-100'
                          }`}
                          title="ہوم پیج پر اس کیٹیگری کی ادویات کے سلائیڈر کو دکھائیں یا چھپائیں"
                        >
                          <Sliders className="w-3.5 h-3.5 text-blue-600" />
                          <span>سلائیڈر: {cat.showRowOnHome !== false ? 'آن' : 'آف'}</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Actions */}
                  <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => handleOpenEditCategory(cat)}
                      className="flex-1 py-2 px-3 rounded-xl bg-gray-50 hover:bg-pansar-50 text-pansar-800 hover:text-pansar-950 font-bold text-xs font-urdu transition flex items-center justify-center gap-1.5 border border-gray-200 cursor-pointer"
                    >
                      <Edit3 className="w-3.5 h-3.5 text-pansar-600" />
                      <span>ترمیم کریں (Edit)</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDeleteCategory(cat)}
                      className="py-2 px-3 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs font-urdu transition flex items-center justify-center gap-1.5 border border-rose-200 cursor-pointer"
                      title="کیٹیگری حذف کریں"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>حذف</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SUB-TAB 2: AILMENTS / HEALTH CONDITIONS */}
      {activeSubTab === 'ailments' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between text-xs text-gray-500 font-urdu px-2">
            <span>کل امراض و شکایات فلٹرز: <strong>{(ailments || []).length}</strong></span>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('کیا آپ امراض فلٹرز کو اصل حالت پر ری سیٹ کرنا چاہتے ہیں؟')) {
                  resetAilmentsToDefaults();
                  showAlert('امراض فلٹرز اصل ڈیفالٹ پر ری سیٹ ہو گئے!');
                }
              }}
              className="text-rose-600 hover:text-rose-800 flex items-center gap-1 cursor-pointer"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>ڈیفالٹ امراض بحال کریں</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(ailments || []).map((ail) => {
              const taggedCount = (products || []).filter(p => (p.ailments || []).includes(ail.id)).length;
              return (
                <div
                  key={ail.id}
                  className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200 hover:border-emerald-300 transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                        #{ail.id}
                      </span>
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 font-urdu">
                        {taggedCount} پروڈکٹس
                      </span>
                    </div>
                    <h4 className="font-black text-gray-900 text-sm font-urdu mb-0.5">
                      {ail.nameUrdu}
                    </h4>
                    <p className="text-xs text-gray-500 font-sans">
                      {ail.nameEn}
                    </p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => handleOpenEditAilment(ail)}
                      className="p-1.5 rounded-lg bg-gray-100 hover:bg-pansar-100 text-gray-700 hover:text-pansar-900 transition"
                      title="ترمیم کریں"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteAilment(ail)}
                      className="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 transition"
                      title="حذف کریں"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SUB-TAB 3: MIZAJ PROFILES */}
      {activeSubTab === 'mizaj' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between text-xs text-gray-500 font-urdu px-2">
            <span>قانون مفرد اعضاء کے مزاج: <strong>{(mizajList || []).length}</strong></span>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('کیا آپ مزاج کو اصل حالت پر ری سیٹ کرنا چاہتے ہیں؟')) {
                  resetMizajToDefaults();
                  showAlert('مزاج کی تفصیلات اصل ڈیفالٹ پر ری سیٹ ہو گئیں!');
                }
              }}
              className="text-rose-600 hover:text-rose-800 flex items-center gap-1 cursor-pointer"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>ڈیفالٹ مزاج بحال کریں</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(mizajList || []).map((m) => {
              const count = (products || []).filter(p => p.mizaj === m.id).length;
              return (
                <div
                  key={m.id}
                  className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200 hover:border-amber-300 transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                        {m.id}
                      </span>
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 font-urdu">
                        {count} پروڈکٹس
                      </span>
                    </div>
                    <h4 className="font-black text-gray-900 text-sm font-urdu mb-0.5">
                      {m.nameUrdu}
                    </h4>
                    <p className="text-xs text-gray-500 font-sans">
                      {m.nameEn}
                    </p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => handleOpenEditMizaj(m)}
                      className="text-xs font-bold text-pansar-700 hover:text-pansar-900 flex items-center gap-1 font-urdu"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      <span>عنوان ایڈٹ کریں</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 1: ADD / EDIT CATEGORY MODAL                                        */}
      {/* ========================================================================= */}
      {categoryModalOpen && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-fade-in cursor-pointer"
          onClick={() => setCategoryModalOpen(false)}
        >
          <div 
            className="relative bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden border border-gray-100 my-8 cursor-default max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-5 sm:p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/70">
              <div className="flex items-center gap-2">
                <FolderTree className="w-5 h-5 text-pansar-800" />
                <h3 className="text-lg font-black text-gray-900 font-title">
                  {editingCategory ? 'کیٹیگری میں ترمیم کریں (Edit Category)' : 'نئی کیٹیگری بنائیں (Add Category)'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setCategoryModalOpen(false)}
                className="w-8 h-8 rounded-full bg-white hover:bg-rose-50 text-gray-500 hover:text-rose-600 flex items-center justify-center shadow-sm border border-gray-200 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveCategory} className="p-6 overflow-y-auto space-y-5 flex-1 font-urdu">
              
              {/* Names row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    کیٹیگری کا اردو نام *
                  </label>
                  <input
                    type="text"
                    required
                    value={categoryForm.nameUrdu}
                    onChange={(e) => setCategoryForm({ ...categoryForm, nameUrdu: e.target.value })}
                    placeholder="مثلاً: خالص جڑی بوٹیاں"
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu focus:border-pansar-700 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    انگریزی نام (English Name)
                  </label>
                  <input
                    type="text"
                    value={categoryForm.nameEn}
                    onChange={(e) => {
                      const name = e.target.value;
                      setCategoryForm(prev => ({
                        ...prev,
                        nameEn: name,
                        id: editingCategory ? prev.id : name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                      }));
                    }}
                    placeholder="e.g. Raw Herbs"
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans text-left dir-ltr focus:border-pansar-700 focus:outline-none"
                  />
                </div>
              </div>

              {/* ID / Slug & Item Count Badge */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    شناختی کوڈ / ID (URL Slug)
                  </label>
                  <input
                    type="text"
                    disabled={!!editingCategory}
                    value={categoryForm.id}
                    onChange={(e) => setCategoryForm({ ...categoryForm, id: e.target.value })}
                    placeholder="e.g. raw-herbs"
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-mono text-left dir-ltr bg-gray-50 focus:border-pansar-700 focus:outline-none"
                  />
                  <span className="text-[10px] text-gray-400 mt-1 block">
                    {editingCategory ? 'شناختی کوڈ کو بعد میں تبدیل نہیں کیا جا سکتا تاکہ پروڈکٹس محفوظ رہیں۔' : 'انگریزی نام لکھنے پر خودکار بن جائے گا۔'}
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    تعداد کا لیبل (Item Count Badge)
                  </label>
                  <input
                    type="text"
                    value={categoryForm.itemCount}
                    onChange={(e) => setCategoryForm({ ...categoryForm, itemCount: e.target.value })}
                    placeholder="مثلاً: 100+ اشیاء"
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs focus:border-pansar-700 focus:outline-none"
                  />
                </div>
              </div>

              {/* Taglines */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  اردو ٹیگ لائن / مختصر تعارف
                </label>
                <textarea
                  rows="2"
                  value={categoryForm.taglineUrdu}
                  onChange={(e) => setCategoryForm({ ...categoryForm, taglineUrdu: e.target.value })}
                  placeholder="مثلاً: قدرتی، صاف شدہ اور با اثر جڑی بوٹیاں"
                  className="w-full p-2.5 rounded-xl border border-gray-200 text-xs focus:border-pansar-700 focus:outline-none"
                />
              </div>

              {/* Icon Selector */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  علامت / آئیکون منتخب کریں (Icon)
                </label>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {Object.keys(ICON_MAP).map((iconKey) => {
                    const CurrIcon = ICON_MAP[iconKey];
                    const isSelected = categoryForm.icon === iconKey;
                    return (
                      <button
                        key={iconKey}
                        type="button"
                        onClick={() => setCategoryForm({ ...categoryForm, icon: iconKey })}
                        className={`p-2.5 rounded-xl border flex flex-col items-center justify-center gap-1 transition ${
                          isSelected
                            ? 'bg-pansar-800 text-amber-300 border-pansar-800 shadow-sm'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-pansar-400'
                        }`}
                        title={iconKey}
                      >
                        <CurrIcon className="w-5 h-5" />
                        <span className="text-[10px] font-sans truncate w-full text-center">
                          {iconKey}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Color Theme Selector */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  رنگین تھیم (Color Theme)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {COLOR_THEMES.map((theme) => {
                    const isSelected = categoryForm.bgGradient === theme.bgGradient;
                    return (
                      <button
                        key={theme.id}
                        type="button"
                        onClick={() => setCategoryForm({ 
                          ...categoryForm, 
                          bgGradient: theme.bgGradient,
                          accentColor: theme.accentColor
                        })}
                        className={`p-2 rounded-xl border text-xs text-right transition flex items-center justify-between ${
                          isSelected
                            ? 'border-pansar-800 bg-pansar-50 font-bold text-pansar-900'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                      >
                        <span className="text-[11px] truncate">{theme.label}</span>
                        {isSelected && <Check className="w-3.5 h-3.5 text-pansar-800 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Optional Category Image Upload */}
              <ImageUploadField
                label="کیٹیگری کی اپنی تصویر (اختیاری - Category Image)"
                value={categoryForm.image || ''}
                onChange={(val) => setCategoryForm({ ...categoryForm, image: val })}
                maxWidth={400}
                previewShape="square"
                helperText="اگر آپ آئیکون کے بجائے اس کیٹیگری کے لیے اپنی مرضی کی تصویر لگانا چاہتے ہیں تو یہاں اپلوڈ کریں۔"
              />

              {/* Home Page Display & Visibility Settings */}
              <div className="bg-emerald-50/70 p-4 rounded-2xl border border-emerald-200/80 space-y-3">
                <div className="flex items-center gap-2 text-xs font-bold text-emerald-950 font-title">
                  <Sparkles className="w-4 h-4 text-emerald-700" />
                  <span>ہوم پیج ڈسپلے اور وزبلٹی سیٹنگز (Home Page Settings)</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Show in Category Grid */}
                  <label className="flex items-center gap-2.5 p-3 rounded-xl bg-white border border-emerald-100 cursor-pointer shadow-sm hover:border-emerald-300 transition">
                    <input
                      type="checkbox"
                      checked={categoryForm.showOnHome !== false}
                      onChange={(e) => setCategoryForm({ ...categoryForm, showOnHome: e.target.checked })}
                      className="w-4 h-4 text-pansar-700 rounded border-gray-300 focus:ring-pansar-500 cursor-pointer"
                    />
                    <div className="text-[11px] leading-tight font-urdu">
                      <span className="font-bold text-gray-800 block">ہوم کیٹیگری گرڈ</span>
                      <span className="text-gray-500 text-[10px]">باکسز میں دکھائیں</span>
                    </div>
                  </label>

                  {/* Show Product Carousel Row */}
                  <label className="flex items-center gap-2.5 p-3 rounded-xl bg-white border border-emerald-100 cursor-pointer shadow-sm hover:border-emerald-300 transition">
                    <input
                      type="checkbox"
                      checked={categoryForm.showRowOnHome !== false}
                      onChange={(e) => setCategoryForm({ ...categoryForm, showRowOnHome: e.target.checked })}
                      className="w-4 h-4 text-pansar-700 rounded border-gray-300 focus:ring-pansar-500 cursor-pointer"
                    />
                    <div className="text-[11px] leading-tight font-urdu">
                      <span className="font-bold text-gray-800 block">ہوم پروڈکٹ سلائیڈر</span>
                      <span className="text-gray-500 text-[10px]">پروڈکٹس کی قطار لائیں</span>
                    </div>
                  </label>

                  {/* Order Number */}
                  <div className="p-3 rounded-xl bg-white border border-emerald-100 shadow-sm flex flex-col justify-between">
                    <label className="block text-[11px] font-bold text-gray-800 font-urdu">
                      ترتیب نمبر (Order)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={categoryForm.order || 1}
                      onChange={(e) => setCategoryForm({ ...categoryForm, order: Number(e.target.value) || 1 })}
                      className="w-full p-1 rounded-lg border border-gray-200 text-xs text-center font-mono focus:border-pansar-700 focus:outline-none"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-emerald-800 font-urdu leading-tight">
                  نوٹ: اگر آپ کسی کیٹیگری کو وقتی طور پر ہوم پیج سے ہٹانا چاہتے ہیں تو اوپر والے دونوں چیک باکسز بند کر دیں۔
                </p>
              </div>

              {/* Live Preview Card */}
              <div className="border-t border-gray-100 pt-4">
                <label className="block text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                  <Eye className="w-4 h-4 text-emerald-600" />
                  <span>ہوم پیج پر کیسا نظر آئے گا؟ (Live Preview)</span>
                </label>
                
                <div className="max-w-xs mx-auto rounded-3xl bg-white p-5 shadow-md border border-emerald-900/10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 rounded-2xl bg-pansar-700 text-amber-300 flex items-center justify-center overflow-hidden">
                      {categoryForm.image ? (
                        <img src={categoryForm.image} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <PreviewIcon className="w-6 h-6" />
                      )}
                    </div>
                    <span className="text-[11px] font-semibold text-pansar-700 font-title">
                      {categoryForm.itemCount || '0 اشیاء'}
                    </span>
                  </div>

                  <h3 className="font-black text-lg text-gray-900 font-title mb-1">
                    {categoryForm.nameUrdu || 'کیٹیگری کا نام'}
                  </h3>

                  <p className="text-xs text-gray-500 font-urdu leading-relaxed line-clamp-2">
                    {categoryForm.taglineUrdu || 'اردو ٹیگ لائن اور تعارف یہاں دکھائی دے گا۔'}
                  </p>

                  <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-pansar-800">
                    <span>اشیاء دیکھیں</span>
                    <ArrowLeft className="w-4 h-4" />
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="border-t border-gray-100 pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setCategoryModalOpen(false)}
                  className="py-2.5 px-5 rounded-xl border border-gray-200 text-gray-700 font-bold text-xs hover:bg-gray-100 transition cursor-pointer"
                >
                  منسوخ کریں
                </button>
                <button
                  type="submit"
                  className="py-2.5 px-6 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs shadow-md transition flex items-center gap-1.5 cursor-pointer"
                >
                  <Check className="w-4 h-4 text-amber-300" />
                  <span>{editingCategory ? 'تبدیلیاں محفوظ کریں' : 'کیٹیگری بنائیں'}</span>
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 2: ADD / EDIT AILMENT MODAL                                         */}
      {/* ========================================================================= */}
      {ailmentModalOpen && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in cursor-pointer"
          onClick={() => setAilmentModalOpen(false)}
        >
          <div 
            className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl p-6 border border-gray-100 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-3">
              <h3 className="font-black text-base text-gray-900 font-title">
                {editingAilment ? 'مرض فلٹر ایڈٹ کریں' : 'نیا مرض / شکایت فلٹر بنائیں'}
              </h3>
              <button onClick={() => setAilmentModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveAilment} className="space-y-4 font-urdu">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  مرض یا شکایت کا اردو نام *
                </label>
                <input
                  type="text"
                  required
                  value={ailmentForm.nameUrdu}
                  onChange={(e) => setAilmentForm({ ...ailmentForm, nameUrdu: e.target.value })}
                  placeholder="مثلاً: شوگر و ذیابیطس"
                  className="w-full p-2.5 rounded-xl border border-gray-200 text-xs focus:border-pansar-700 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  انگریزی نام (English Name)
                </label>
                <input
                  type="text"
                  value={ailmentForm.nameEn}
                  onChange={(e) => setAilmentForm({ ...ailmentForm, nameEn: e.target.value })}
                  placeholder="e.g. Diabetes & Blood Sugar"
                  className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans text-left dir-ltr focus:border-pansar-700 focus:outline-none"
                />
              </div>

              {!editingAilment && (
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    شناختی کوڈ (ID Slug)
                  </label>
                  <input
                    type="text"
                    value={ailmentForm.id}
                    onChange={(e) => setAilmentForm({ ...ailmentForm, id: e.target.value })}
                    placeholder="e.g. diabetes"
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-mono text-left dir-ltr focus:border-pansar-700 focus:outline-none"
                  />
                  <span className="text-[10px] text-gray-400 mt-0.5 block">خالی چھوڑنے پر خودکار بن جائے گا۔</span>
                </div>
              )}

              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAilmentModalOpen(false)}
                  className="py-2 px-4 rounded-xl border border-gray-200 text-xs font-bold hover:bg-gray-100"
                >
                  منسوخ کریں
                </button>
                <button
                  type="submit"
                  className="py-2 px-5 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white text-xs font-bold shadow-md"
                >
                  محفوظ کریں
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 3: EDIT MIZAJ MODAL                                                 */}
      {/* ========================================================================= */}
      {mizajModalOpen && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in cursor-pointer"
          onClick={() => setMizajModalOpen(false)}
        >
          <div 
            className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl p-6 border border-gray-100 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-3">
              <h3 className="font-black text-base text-gray-900 font-title">
                مزاج کی تفصیلات ایڈٹ کریں
              </h3>
              <button onClick={() => setMizajModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveMizaj} className="space-y-4 font-urdu">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  اردو عنوان و مزاج *
                </label>
                <input
                  type="text"
                  required
                  value={mizajForm.nameUrdu}
                  onChange={(e) => setMizajForm({ ...mizajForm, nameUrdu: e.target.value })}
                  className="w-full p-2.5 rounded-xl border border-gray-200 text-xs focus:border-pansar-700 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  انگریزی عنوان (English Name)
                </label>
                <input
                  type="text"
                  value={mizajForm.nameEn}
                  onChange={(e) => setMizajForm({ ...mizajForm, nameEn: e.target.value })}
                  className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans text-left dir-ltr focus:border-pansar-700 focus:outline-none"
                />
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setMizajModalOpen(false)}
                  className="py-2 px-4 rounded-xl border border-gray-200 text-xs font-bold hover:bg-gray-100"
                >
                  منسوخ کریں
                </button>
                <button
                  type="submit"
                  className="py-2 px-5 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white text-xs font-bold shadow-md"
                >
                  محفوظ کریں
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default CategoryManager;
